import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Shared events — another client's suite shown on this client, READ-ONLY
// (server/suiteShares.js). Built for artist / sponsor / promoter accounts whose
// shows sit under other organisers, so one event is never set up twice.
//
// Dual-surface via `scope`, deliberately asymmetric (like SharedDatabasesCard):
//   'admin-client' (Admin → the client → Shared events) — share, relabel, revoke.
//   'my'           (Settings → Shared events)            — READ what you hold.
// Seeing another client's event is not a client's call to make, so self-service
// here is "know exactly what you have", with a line on who to ask.
//
// Mobile-first: one stacked column, ≥40px controls.
// A suite icon is an emoji OR an uploaded image (a data: URL) — only the former
// can be printed inline; an image would spill kilobytes of base64 into the row.
const inlineIcon = (v) => (v && v.length <= 8 && !/^(data:|https?:)/.test(v) ? `${v} ` : '');

export default function SharedSuitesCard({ entityId, scope = 'my' }) {
  const isAdmin = scope === 'admin-client';
  const [view, setView] = useState(null);
  const [denied, setDenied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [suiteId, setSuiteId] = useState('');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [includes, setIncludes] = useState(['campaigns', 'segments']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => (isAdmin ? api.adminSharedSuites(entityId) : api.mySharedSuites(entityId));
  useEffect(() => {
    let alive = true;
    load().then((v) => { if (alive) setView(v); }).catch(() => { if (alive) setDenied(true); });
    return () => { alive = false; };
  }, [entityId, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  if (denied || !view) return null;

  const grants = view.grants || [];
  const options = view.options || [];
  const q = search.trim().toLowerCase();
  const hits = options.filter((o) => !q || o.name.toLowerCase().includes(q) || o.entityName.toLowerCase().includes(q)).slice(0, 40);
  const reset = () => { setAdding(false); setSearch(''); setSuiteId(''); setLabel(''); setNote(''); setIncludes(['campaigns', 'segments']); setError(''); };
  const toggleInc = (k) => setIncludes((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const share = async () => {
    setBusy(true); setError('');
    try { await api.adminGrantSharedSuite(entityId, { suiteId, label, note, includes }); setView(await load()); reset(); }
    catch (e) { setError(e.message || 'Could not share that event.'); }
    setBusy(false);
  };
  const revoke = async (g) => {
    if (!confirm(`Stop sharing “${g.label || g.suiteName}” with this client? Their nav loses it immediately.`)) return;
    try { await api.adminRevokeSharedSuite(entityId, g.id); setView(await load()); } catch (e) { setError(e.message || 'Could not remove it.'); }
  };
  const setInc = async (g, k) => {
    const next = g.includes.includes(k) ? g.includes.filter((x) => x !== k) : [...g.includes, k];
    try { await api.adminUpdateSharedSuite(entityId, g.id, { includes: next }); setView(await load()); } catch (e) { setError(e.message || 'Could not save.'); }
  };
  const INC = [['campaigns', 'Campaigns (read-only list)'], ['segments', 'Segments (read-only list)']];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Shared events</h3>
        {view.flagOn === false && <span style={offPill} title="The 'Shared events (read-only)' switch is off for this client — nothing here reaches them until it is on.">SWITCH OFF</span>}
      </div>
      <p style={hint}>
        {isAdmin
          ? 'Show this client an event ANOTHER client runs — the organiser’s own suite appears in this client’s nav, read-only: the same dashboards, plus a read-only list of the campaigns and segments linked to that event. The viewer is fenced to the event(s) the suite locks, gets no Owl, goals, alerts or segment creation on it, and can never send. Built for artist, sponsor and promoter accounts.'
          : 'Events another organiser runs that Howler has shared with you. You see the same dashboards they do for that event, and a read-only list of the campaigns and segments linked to it — nothing else of theirs, and nothing you can edit or send.'}
      </p>

      {grants.length === 0
        ? <div style={{ ...hint, marginTop: 10 }}>{isAdmin ? 'No events shared with this client yet.' : 'No events have been shared with you.'}</div>
        : grants.map((g) => (
          <div key={g.id} style={row}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{inlineIcon(g.suiteIcon)}{g.label || g.suiteName}</span>
                <span style={pill}>read-only</span>
                {g.missing && <span style={offPill}>event deleted</span>}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
                Run by <b>{g.ownerName || '—'}</b>{g.label && g.suiteName ? ` · their suite “${g.suiteName}”` : ''}
                {' · '}{g.includes.length ? `includes ${g.includes.join(' + ')}` : 'dashboards only'}
              </div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                  {INC.map(([k, lab]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, minHeight: 32, cursor: 'pointer' }}>
                      <input type="checkbox" checked={g.includes.includes(k)} onChange={() => setInc(g, k)} /> {lab}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {isAdmin && <button style={revokeBtn} onClick={() => revoke(g)}>Stop sharing</button>}
          </div>
        ))}

      {isAdmin && (adding ? (
        <div style={box}>
          <label style={lbl}>Which event?</label>
          <input style={input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search another client’s events by name or client…" />
          <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
            {hits.map((o) => (
              <label key={o.suiteId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', minHeight: 40, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name="shared-suite" checked={suiteId === o.suiteId} onChange={() => setSuiteId(o.suiteId)} />
                <span style={{ flex: 1, minWidth: 0 }}>{inlineIcon(o.icon)}{o.name} <span style={{ color: 'var(--muted)' }}>· {o.entityName}</span></span>
              </label>
            ))}
            {!hits.length && <div style={hint}>No other client’s event matches that.</div>}
          </div>
          <label style={{ ...lbl, marginTop: 10, display: 'block' }}>Show it as (optional)</label>
          <input style={input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Leave blank to use the event’s own name" />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
            {INC.map(([k, lab]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, minHeight: 32, cursor: 'pointer' }}>
                <input type="checkbox" checked={includes.includes(k)} onChange={() => toggleInc(k)} /> {lab}
              </label>
            ))}
          </div>
          <label style={{ ...lbl, marginTop: 10, display: 'block' }}>Why is this allowed?</label>
          <textarea style={{ ...input, minHeight: 56, resize: 'vertical', fontFamily: 'inherit' }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Artist account — Kunye headlines this show; organiser agreed 8 Sep. (Internal only.)" />
          <div style={hint}>The suite must lock its event(s): the viewer is fenced to exactly those, never the organiser’s other shows. A suite with no event lock stays blocked for the viewer.</div>
          {error && <div style={errS}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button style={primaryBtn} disabled={busy || !suiteId} onClick={share}>{busy ? 'Sharing…' : 'Share this event'}</button>
            <button style={mini} disabled={busy} onClick={reset}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {error && <div style={{ ...errS, marginBottom: 8 }}>{error}</div>}
          <button style={primaryBtn} onClick={() => { setAdding(true); setError(''); }}>＋ Share an event with this client</button>
        </div>
      ))}
      {!isAdmin && grants.length > 0 && <p style={{ ...hint, marginTop: 10 }}>Shared events sit in your event list marked <b>shared</b>. To add or remove one, speak to your Howler account manager.</p>}
    </div>
  );
}

const hint = { fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, margin: '6px 0 0' };
const lbl = { fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)' };
const box = { border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', marginTop: 10, background: 'var(--card)' };
const row = { display: 'flex', alignItems: 'flex-start', gap: 10, border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', flexWrap: 'wrap', marginTop: 8 };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', minHeight: 40, border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--text)', marginTop: 4 };
const primaryBtn = { padding: '10px 14px', minHeight: 40, borderRadius: 8, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const mini = { padding: '10px 12px', minHeight: 40, borderRadius: 8, border: '1.5px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const revokeBtn = { ...mini, color: 'var(--danger,#dc2626)', borderColor: 'var(--danger,#dc2626)' };
const pill = { fontSize: 10.5, fontWeight: 800, borderRadius: 980, padding: '2px 9px', background: 'rgba(var(--brand-rgb),0.12)', color: 'var(--brand)' };
const offPill = { fontSize: 10.5, fontWeight: 800, borderRadius: 980, padding: '2px 9px', background: 'rgba(220,38,38,0.12)', color: 'var(--danger,#dc2626)' };
const errS = { fontSize: 12.5, color: 'var(--danger,#dc2626)', fontWeight: 600, marginTop: 6 };
